import clsx from 'clsx';
import { cn, parseTime } from '../../lib/helpers';
import { Button } from '../buttons/Button';
import { Color, Lecture } from '../../lib/types.ts';
import { LectureVideoPlayer } from '../video/LectureVideoPlayer.tsx';
import { UilLock, UilPlay } from '@iconscout/react-unicons';
import { useState } from 'react';
import MethodologyDialog from '../dialogs/MethodologyDialog.tsx';

interface PlayLectureCardProps {
    color?: Color | 'product';
    lecture: Lecture;

    isActive?: boolean;
    isLast?: boolean;
    isNextCompleted?: boolean;

    onLessonStart?: () => void;
    onLessonEnd?: () => void;

    onLessonVideoStart?: () => void;
}

export function PlayLectureCard({
    color = 'product',
    lecture: {
        name,
        methodology,
        video,
        is_locked: isLocked,
        user_completed: isCompleted = false,
    },
    //
    isActive = false,
    onLessonStart,
    onLessonEnd,
    onLessonVideoStart,
}: PlayLectureCardProps) {
    const { minutes, seconds } = parseTime(video?.length);

    const [isMethodologyDialogOpen, setIsMethodologyDialogOpen] =
        useState(false);

    return (
        <div className="flex rounded-2xl bg-white p-4 shadow-sm">
            <div className={clsx('flex w-full flex-wrap items-start')}>
                <div className="-mt-1">
                    <h4 className={cn(isLocked && 'opacity-50')}>{name}</h4>
                    <p
                        className={cn(
                            isLocked && 'opacity-50',
                            'text-sm text-ink-800'
                        )}
                    >
                        {minutes > 0 && <>{minutes}&nbsp;min</>} {seconds}
                        &nbsp;s
                    </p>
                </div>
                <div className="ml-auto mr-0 flex items-center justify-center gap-2 self-stretch">
                    {/* Single methodology - only for active lesson */}
                    {isActive && !isLocked ? (
                        <Button
                            color={color}
                            className="w-28"
                            variant="secondary"
                            onClick={() => setIsMethodologyDialogOpen(true)}
                            disabled={isLocked}
                        >
                            {isLocked ? <UilLock size={20} /> : <>Jak na to?</>}
                        </Button>
                    ) : null}

                    {isActive ? (
                        // Done button for active lesson
                        <Button
                            color={color}
                            className="w-28"
                            variant="primary"
                            onClick={onLessonEnd}
                            disabled={isLocked}
                        >
                            {isLocked ? <UilLock size={20} /> : <>Hotovo</>}
                        </Button>
                    ) : (
                        // Play icon button for inactive lesson
                        <Button
                            color={color}
                            className="aspect-square p-3"
                            variant="secondary"
                            onClick={onLessonStart}
                            disabled={isLocked}
                        >
                            {isLocked ? (
                                <UilLock size={20} />
                            ) : (
                                <UilPlay
                                    aria-label={
                                        isCompleted
                                            ? 'Spustit znovu'
                                            : 'Spustit'
                                    }
                                    size={20}
                                />
                            )}
                        </Button>
                    )}
                </div>
                <div
                    className={clsx(
                        'h-auto w-full overflow-hidden transition-all',
                        isActive ? 'max-h-96' : 'max-h-0'
                    )}
                >
                    <LectureVideoPlayer
                        isActive={isActive}
                        video={video}
                        onEnded={onLessonEnd}
                        onFirstPlay={onLessonVideoStart}
                    />
                </div>

                <MethodologyDialog
                    isOpen={isMethodologyDialogOpen}
                    onClose={() => {
                        setIsMethodologyDialogOpen(false);
                    }}
                    heading={name}
                    content={methodology}
                    color={color}
                />
            </div>
        </div>
    );
}
